import React, { useState, useEffect, useReducer } from 'react';
import Sidebar from './Sidebar';
import Calendar from './Calendar';
import Footer from './Footer';
import { DateRange, TimeValue, SidebarOption } from './types';
import { dateRangeReducer, initialDateRangeState } from './dateRangeReducer';
import {
  format,
  isAfter,
  isBefore,
  isSameDay,
  addMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  subDays,
  subMonths,
  subYears,
  startOfYear,
  endOfYear,
} from 'date-fns';
import './dateRangePicker.scss';
import { getStartEndDateFromRange } from '../../utilities/DateUtils';
import { mapOptionToRange } from '../../utilities/TimeRangeMappers';
import { useAppSelector } from '../../hooks/storeHooks';

interface DateRangePickerProps {
  onApply?: (range: { startDate: Date; endDate: Date }) => void;
  onCancel?: () => void;
  initialStartDate?: Date;
  initialEndDate?: Date;
  className?: string;
  showTimeInputs?: boolean;
  sidebarOptions?: SidebarOption[];
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  onApply,
  onCancel,
  initialStartDate = null,
  initialEndDate = null,
  className = '',
  showTimeInputs = true,
  sidebarOptions,
}) => {
  // Initialize with current month and next month
  const currentDate = new Date();
  const nextMonthDate = addMonths(currentDate, 1);
  const popDate = useAppSelector((state: any) => state.WellTestChartDataSlice?.popDate);
  const curCompDate = useAppSelector((state: any) => state.WellTestChartDataSlice?.currentInstall);

  const resolvedStartDisplayDate = initialStartDate ? startOfMonth(initialStartDate) : currentDate;
  const resolvedEndDisplayDate = initialEndDate ? startOfMonth(initialEndDate) : nextMonthDate;

  const [state, dispatch] = useReducer(dateRangeReducer, {
    ...initialDateRangeState,
    startDate: initialStartDate ?? null,
    endDate: initialEndDate ?? null,
    hoveredDate: null,
    startDisplayDate: resolvedStartDisplayDate,
    endDisplayDate: resolvedEndDisplayDate,
    selectedSidebarOption: null,
  });

  const [startTime, setStartTime] = useState<TimeValue>({
    hours: '00',
    minutes: '00',
    seconds: '00',
    period: 'AM',
  });

  const [endTime, setEndTime] = useState<TimeValue>({
    hours: '00',
    minutes: '00',
    seconds: '00',
    period: 'AM',
  });

  const [lastConfirmedRange, setLastConfirmedRange] = useState<DateRange>({
    startDate: state.startDate,
    endDate: state.endDate,
  });

  useEffect(() => {
    // Ensure the second calendar always shows the next month
    if (state.startDisplayDate && state.endDisplayDate) {
      const startMonth = state.startDisplayDate.getMonth();
      const endMonth = state.endDisplayDate.getMonth();
      const startYear = state.startDisplayDate.getFullYear();
      const endYear = state.endDisplayDate.getFullYear();

      // If they're the same month or the end date is before the start date
      if (
        (startMonth === endMonth && startYear === endYear) ||
        startYear > endYear ||
        (startYear === endYear && startMonth >= endMonth)
      ) {
        dispatch({
          type: 'SET_DISPLAY_DATES',
          payload: {
            startDisplayDate: state.startDisplayDate,
            endDisplayDate: addMonths(state.startDisplayDate, 1),
          },
        });
      }
    }
  }, [state.startDisplayDate, state.endDisplayDate]);

  // Helper function to convert TimeValue to minutes for comparison
  const timeToMinutes = (time: TimeValue): number => {
    const hours = parseInt(time.hours, 10);
    const minutes = parseInt(time.minutes, 10);
    const seconds = parseInt(time.seconds, 10);

    // Convert to 24-hour format
    let hour24 = hours;
    if (time.period === 'PM' && hours !== 12) {
      hour24 += 12;
    } else if (time.period === 'AM' && hours === 12) {
      hour24 = 0;
    }

    return hour24 * 60 * 60 + minutes * 60 + seconds;
  };

  // Handle start time change with validation
  const handleStartTimeChange = (time: TimeValue) => {
    setStartTime(time);

    // If dates are the same, validate that end time is not before start time
    if (state.startDate && state.endDate && isSameDay(state.startDate, state.endDate)) {
      const startMinutes = timeToMinutes(time);
      const endMinutes = timeToMinutes(endTime);

      if (startMinutes > endMinutes) {
        // If start time is later than end time, set end time to match start time
        setEndTime(time);
      }
    }
  };

  // Handle end time change with validation
  const handleEndTimeChange = (time: TimeValue) => {
    // If dates are the same, validate that end time is not before start time
    if (state.startDate && state.endDate && isSameDay(state.startDate, state.endDate)) {
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(time);

      if (endMinutes < startMinutes) {
        // If end time is earlier than start time, set end time to match start time
        setEndTime(startTime);
        return;
      }
    }

    setEndTime(time);
  };

  useEffect(() => {
    if (state.startDate) {
      const startHours = state.startDate.getHours();
      const startMinutes = state.startDate.getMinutes();
      const startSeconds = state.startDate.getSeconds();

      setStartTime({
        hours: startHours === 0 || startHours === 12 ? '12' : (startHours % 12).toString().padStart(2, '0'),
        minutes: startMinutes.toString().padStart(2, '0'),
        seconds: startSeconds.toString().padStart(2, '0'),
        period: startHours >= 12 ? 'PM' : 'AM',
      });
    }

    if (state.endDate) {
      const endHours = state.endDate.getHours();
      const endMinutes = state.endDate.getMinutes();
      const endSeconds = state.endDate.getSeconds();

      setEndTime({
        hours: endHours === 0 || endHours === 12 ? '12' : (endHours % 12).toString().padStart(2, '0'),
        minutes: endMinutes.toString().padStart(2, '0'),
        seconds: endSeconds.toString().padStart(2, '0'),
        period: endHours >= 12 ? 'PM' : 'AM',
      });
    }
  }, [state.startDate, state.endDate]);

  const handleDateClick = (date: Date) => {
    if (!state.startDate || (state.startDate && state.endDate)) {
      // Start a new selection
      dispatch({ type: 'SET_START_DATE', payload: { date } });
      dispatch({ type: 'SET_END_DATE', payload: { date: null } });
      // Clear sidebar selection as this is a custom date selection
      dispatch({ type: 'SET_SELECTED_SIDEBAR_OPTION', payload: { option: null } });
    } else {
      // Complete the selection
      if (isBefore(date, state.startDate)) {
        dispatch({ type: 'SET_START_DATE', payload: { date } });

        const adjustedEnd = new Date(state.startDate);
        if (adjustedEnd.getHours() < 12) {
          adjustedEnd.setHours(adjustedEnd.getHours() + 12);
        }

        dispatch({ type: 'SET_END_DATE', payload: { date: adjustedEnd } });
      } else {
        const adjustedDate = new Date(date);
        if (adjustedDate.getHours() < 12) {
          adjustedDate.setHours(adjustedDate.getHours() + 12);
        }

        dispatch({ type: 'SET_END_DATE', payload: { date: adjustedDate } });

        // If selecting the same day, ensure end time is not before start time
        if (isSameDay(date, state.startDate)) {
          const startMinutes = timeToMinutes(startTime);
          const endMinutes = timeToMinutes(endTime);

          if (endMinutes < startMinutes) {
            // If end time is earlier than start time, set end time to match start time
            setEndTime(startTime);
          }
        }
      }
      // Clear sidebar selection as this is a custom date selection
      dispatch({ type: 'SET_SELECTED_SIDEBAR_OPTION', payload: { option: null } });
    }
  };

  const handleDateHover = (date: Date) => {
    if (state.startDate && !state.endDate) {
      dispatch({ type: 'SET_HOVERED_DATE', payload: { date } });
    }
  };

  // Handle manual changes in footer date inputs (start)
  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      // Create a copy to avoid mutating the original date
      const dateCopy = new Date(date);
      dateCopy.setHours(0, 0, 0, 0);

      dispatch({ type: 'SET_START_DATE', payload: { date: dateCopy } });

      // If we have an end date and it's before the new start date, adjust it
      if (state.endDate && isBefore(state.endDate, dateCopy)) {
        dispatch({ type: 'SET_END_DATE', payload: { date: dateCopy } });
      } else if (state.endDate && isSameDay(state.endDate, dateCopy)) {
        // If dates are the same, validate time
        const startMinutes = timeToMinutes(startTime);
        const endMinutes = timeToMinutes(endTime);

        if (endMinutes < startMinutes) {
          // If end time is earlier than start time, set end time to match start time
          setEndTime(startTime);
        }
      }

      // Update the display month to show the selected date
      dispatch({
        type: 'SET_DISPLAY_DATES',
        payload: {
          startDisplayDate: startOfMonth(dateCopy),
          endDisplayDate: state.endDisplayDate,
        },
      });

      // Clear sidebar selection as this is a custom date selection
      dispatch({ type: 'SET_SELECTED_SIDEBAR_OPTION', payload: { option: null } });
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      // Create a copy to avoid mutating the original date
      const dateCopy = new Date(date);
      dateCopy.setHours(23, 59, 59, 999);

      // If the end date is before the start date, set both to the start date
      if (state.startDate && isBefore(dateCopy, state.startDate)) {
        dispatch({ type: 'SET_END_DATE', payload: { date: state.startDate } });
      } else {
        dispatch({ type: 'SET_END_DATE', payload: { date: dateCopy } });

        // If dates are the same, validate time
        if (state.startDate && isSameDay(dateCopy, state.startDate)) {
          const startMinutes = timeToMinutes(startTime);
          const endMinutes = timeToMinutes(endTime);

          if (endMinutes < startMinutes) {
            // If end time is earlier than start time, set end time to match start time
            setEndTime(startTime);
          }
        }
      }

      // Update the display month to show the selected date
      dispatch({
        type: 'SET_DISPLAY_DATES',
        payload: {
          startDisplayDate: state.startDisplayDate,
          endDisplayDate: startOfMonth(dateCopy),
        },
      });

      // Clear sidebar selection as this is a custom date selection
      dispatch({ type: 'SET_SELECTED_SIDEBAR_OPTION', payload: { option: null } });
    }
  };

  const handleApply = () => {
    if (state.startDate && state.endDate) {
      const startDateTime = new Date(state.startDate);
      const endDateTime = new Date(state.endDate);

      // If time inputs are shown, set time values from the pickers; otherwise keep existing times
      if (showTimeInputs) {
        const parseTimeToHour = (t: TimeValue) =>
          t.period === 'AM'
            ? t.hours === '12'
              ? 0
              : parseInt(t.hours, 10)
            : t.hours === '12'
              ? 12
              : parseInt(t.hours, 10) + 12;

        startDateTime.setHours(
          parseTimeToHour(startTime),
          parseInt(startTime.minutes, 10),
          parseInt(startTime.seconds, 10),
        );
        endDateTime.setHours(parseTimeToHour(endTime), parseInt(endTime.minutes, 10), parseInt(endTime.seconds, 10));
      }

      // Ensure end date is after start date
      if (isAfter(startDateTime, endDateTime)) {
        // If end time is before start time on the same day, set end time to match start time
        if (isSameDay(startDateTime, endDateTime)) {
          endDateTime.setHours(startDateTime.getHours(), startDateTime.getMinutes(), startDateTime.getSeconds());
          // keep endTime in sync with startTime
          setEndTime(startTime);
        }
      }

      setLastConfirmedRange({
        startDate: startDateTime,
        endDate: endDateTime,
      });

      if (onApply) {
        onApply({
          startDate: startDateTime,
          endDate: endDateTime,
        });
      }
    }
  };

  const handleCancel = () => {
    dispatch({ type: 'SET_START_DATE', payload: { date: lastConfirmedRange.startDate } });
    dispatch({ type: 'SET_END_DATE', payload: { date: lastConfirmedRange.endDate } });
    if (onCancel) {
      onCancel();
    }
  };

  const formatDateDisplay = (date: Date | null): string => {
    if (!date) return '';
    return format(date, 'MMM d, yyyy');
  };

  const handleStartMonthChange = (date: Date) => {
    dispatch({
      type: 'SET_DISPLAY_DATES',
      payload: {
        startDisplayDate: date,
        endDisplayDate: state.endDisplayDate,
      },
    });
  };

  const handleEndMonthChange = (date: Date) => {
    dispatch({
      type: 'SET_DISPLAY_DATES',
      payload: {
        startDisplayDate: state.startDisplayDate,
        endDisplayDate: date,
      },
    });
  };

  // -------------------------
  // Sidebar option click handler (moved back to original placement)
  // -------------------------
  const handleSidebarOptionClick = (option: SidebarOption) => {
    const now = new Date();
    let startDate: Date;
    let endDate: Date;

    switch (option) {
      case 'Today':
        startDate = new Date(now);
        endDate = new Date(now);
        break;

      case 'Yesterday':
        startDate = subDays(now, 1);
        endDate = subDays(now, 1);
        break;

      case 'This week':
        startDate = startOfWeek(now, { weekStartsOn: 1 });
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;

      case 'Last week': {
        const lastWeekRef = subDays(now, 7);
        startDate = startOfWeek(lastWeekRef, { weekStartsOn: 1 });
        endDate = endOfWeek(lastWeekRef, { weekStartsOn: 1 });
        break;
      }

      case 'This month':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;

      case 'Last month': {
        const lastMonth = subMonths(now, 1);
        startDate = startOfMonth(lastMonth);
        endDate = endOfMonth(lastMonth);
        break;
      }

      case 'This year':
        startDate = startOfYear(now);
        endDate = endOfYear(now);
        break;

      case 'Last year': {
        const lastYear = subYears(now, 1);
        startDate = startOfYear(lastYear);
        endDate = endOfYear(lastYear);
        break;
      }

      case 'All time':
        // arbitrary early date representing "all"
        startDate = new Date(2000, 0, 1);
        endDate = new Date(now);
        break;

      case 'POP':
        startDate = popDate ? new Date(popDate) : new Date(now);
        endDate = new Date(now);
        break;

      case 'Current compl.':
        startDate = curCompDate ? new Date(curCompDate) : new Date(now);
        endDate = new Date(now);
        break;

      default:
        // fallback to existing mapping util if available (preserves your mapOptionToRange usage)
        try {
          const range = mapOptionToRange(option);
          const dateRange = getStartEndDateFromRange(range);
          startDate = dateRange.startDate;
          endDate = dateRange.endDate;
        } catch (err) {
          startDate = new Date(now);
          endDate = new Date(now);
        }
        break;
    }

    // Normalize to full-day bounds for consistent UI (except when user uses time inputs)
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    // Update reducer
    const startCopy = new Date(startDate);
    const endCopy = new Date(endDate);
    dispatch({ type: 'SET_START_DATE', payload: { date: startCopy } });
    dispatch({ type: 'SET_END_DATE', payload: { date: endCopy } });
    dispatch({ type: 'SET_SELECTED_SIDEBAR_OPTION', payload: { option } });

    // Update display months so calendars show the selected range
    dispatch({
      type: 'SET_DISPLAY_DATES',
      payload: { startDisplayDate: startOfMonth(startCopy), endDisplayDate: startOfMonth(endCopy) },
    });
  };

  // Calculate if either month shows 6 weeks (for CSS/layout)
  const calculateWeeksInMonth = (date: Date): number =>
    Math.ceil(
      (endOfWeek(endOfMonth(date)).getTime() - startOfWeek(startOfMonth(date)).getTime()) / (7 * 24 * 60 * 60 * 1000),
    );

  const hasSixWeeks =
    calculateWeeksInMonth(state.startDisplayDate) === 6 || calculateWeeksInMonth(state.endDisplayDate) === 6;

  return (
    <section className={`date-range-picker ${className} ${hasSixWeeks ? 'has-six-weeks' : 'has-five-weeks'}`}>
      <Sidebar
        selectedOption={state.selectedSidebarOption}
        onOptionClick={handleSidebarOptionClick}
        options={sidebarOptions}
      />

      <div className='date-range-content'>
        <div className='calendars-container'>
          <Calendar
            startDate={state.startDate}
            endDate={state.endDate}
            hoveredDate={state.hoveredDate}
            startDisplayDate={state.startDisplayDate}
            onDateClick={handleDateClick}
            onDateHover={handleDateHover}
            onMonthChange={handleStartMonthChange}
            showTimeInputs={showTimeInputs}
            startTime={startTime}
            onStartTimeChange={handleStartTimeChange}
            mode='start'
          />
          <div
            style={{
              borderLeft: 'solid 1px #4a5463',
              height: '100%',
              alignSelf: 'stretch',
            }}
          />
          <Calendar
            startDate={state.startDate}
            endDate={state.endDate}
            hoveredDate={state.hoveredDate}
            startDisplayDate={state.endDisplayDate}
            onDateClick={handleDateClick}
            onDateHover={handleDateHover}
            onMonthChange={handleEndMonthChange}
            showTimeInputs={showTimeInputs}
            endTime={endTime}
            onEndTimeChange={handleEndTimeChange}
            mode='end'
          />
        </div>
        <Footer
          startDate={state.startDate ? formatDateDisplay(state.startDate) : ''}
          endDate={state.endDate ? formatDateDisplay(state.endDate) : ''}
          onApply={handleApply}
          onCancel={handleCancel}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
        />
      </div>
    </section>
  );
};

export default DateRangePicker;
